import { useEffect, useRef, useState } from 'react';
import { ImageUploader } from '../../components/ImageUploader';
import { Menu, MenuItem, MenuLabel } from '../../components/ui';
import {
  CHOICE_TYPES,
  QUESTION_TYPES,
  QUESTION_TYPE_LABELS,
  type Question,
  type QuestionType,
} from '../../lib/types';
import { cx, moveItem } from '../../lib/utils';

interface Props {
  question: Question;
  number: number;
  expanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  issue?: string;
  otherSections: { id: string; title: string }[];
  onToggle: () => void;
  onChange: (patch: Partial<Question>) => void;
  onMove: (delta: -1 | 1) => void;
  onMoveTo: (sectionId: string) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const SHORT_TYPE_LABELS: Record<QuestionType, string> = {
  ...QUESTION_TYPE_LABELS,
  single_choice: 'One choice',
  multiple_choice: 'Checkboxes',
  file_upload: 'File upload',
};

export function QuestionEditor({
  question: q,
  number,
  expanded,
  isFirst,
  isLast,
  issue,
  otherSections,
  onToggle,
  onChange,
  onMove,
  onMoveTo,
  onDuplicate,
  onDelete,
}: Props) {
  const isChoice = CHOICE_TYPES.has(q.type);
  const hasPlaceholder = !isChoice && q.type !== 'file_upload' && q.type !== 'date';
  // Rarely used fields stay tucked away unless they already hold something.
  const [showMore, setShowMore] = useState(() => Boolean(q.quote || q.imageId));

  function changeType(type: QuestionType) {
    const needsOptions = CHOICE_TYPES.has(type);
    onChange({
      type,
      options: needsOptions
        ? q.options?.length
          ? q.options
          : ['Option 1', 'Option 2']
        : undefined,
      placeholder:
        needsOptions || type === 'file_upload' || type === 'date' ? undefined : q.placeholder,
    });
  }

  return (
    <div
      id={`qe-${q.id}`}
      className={cx('question-editor editor-anchor', expanded && 'expanded', issue && 'has-issue')}
    >
      <div className="question-editor-head">
        <button
          type="button"
          className="question-editor-title"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className="question-number">{number}</span>
          <span className="clamp-1">{q.label || <em className="muted">Untitled question</em>}</span>
        </button>
        <div className="row tight">
          <span className="chip">{SHORT_TYPE_LABELS[q.type]}</span>
          {q.imageId && <span className="chip">Image</span>}
          <button
            type="button"
            className={cx('chip chip-toggle', q.required && 'chip-accent')}
            aria-pressed={q.required}
            title={
              q.required ? 'Required — click to make optional' : 'Optional — click to make required'
            }
            onClick={() => onChange({ required: !q.required })}
          >
            {q.required ? 'Required' : 'Optional'}
          </button>
          <button
            type="button"
            className="icon-btn"
            title="Move up"
            aria-label="Move question up"
            disabled={isFirst}
            onClick={() => onMove(-1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="icon-btn"
            title="Move down"
            aria-label="Move question down"
            disabled={isLast}
            onClick={() => onMove(1)}
          >
            ↓
          </button>
          <Menu label="Question actions" trigger="⋯">
            <MenuItem onSelect={onDuplicate}>Duplicate</MenuItem>
            {otherSections.length > 0 && <MenuLabel>Move to section</MenuLabel>}
            {otherSections.map((s) => (
              <MenuItem key={s.id} onSelect={() => onMoveTo(s.id)}>
                {s.title || 'Untitled section'}
              </MenuItem>
            ))}
            <MenuItem onSelect={onDelete} danger>
              Delete
            </MenuItem>
          </Menu>
        </div>
      </div>

      {issue && !expanded && <p className="error-text question-issue">{issue}</p>}

      {expanded && (
        <div className="question-editor-body">
          <div className="field-grid">
            <label className="field span-2">
              <span>Question</span>
              <textarea
                className="textarea"
                rows={2}
                value={q.label}
                autoFocus={!q.label}
                aria-invalid={issue && !q.label.trim() ? true : undefined}
                onChange={(e) => onChange({ label: e.target.value })}
                placeholder="What would you like to ask? Keep it short and clear."
              />
            </label>

            <label className="field">
              <span>Answer type</span>
              <select
                className="select"
                value={q.type}
                onChange={(e) => changeType(e.target.value as QuestionType)}
              >
                {QUESTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {QUESTION_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>

            <label className="field switch-field">
              <input
                type="checkbox"
                className="switch"
                checked={q.required}
                onChange={(e) => onChange({ required: e.target.checked })}
              />
              <span>
                Required
                <small className="muted">
                  {q.required ? 'Clients must answer' : 'Clients can skip it'}
                </small>
              </span>
            </label>

            {isChoice && (
              <div className="field span-2">
                <span>Options</span>
                <OptionsEditor
                  options={q.options ?? []}
                  onChange={(options) => onChange({ options })}
                />
              </div>
            )}

            <label className="field span-2">
              <span>Help text (optional)</span>
              <textarea
                className="textarea"
                rows={2}
                value={q.description ?? ''}
                onChange={(e) => onChange({ description: e.target.value })}
                placeholder="Extra guidance or an example, shown under the question"
              />
            </label>

            {hasPlaceholder && (
              <label className="field span-2">
                <span>Placeholder (optional)</span>
                <input
                  className="input"
                  value={q.placeholder ?? ''}
                  onChange={(e) => onChange({ placeholder: e.target.value })}
                  placeholder="Hint text shown inside the empty answer box"
                />
              </label>
            )}

            {showMore ? (
              <>
                <label className="field span-2">
                  <span>Example / scenario quote (optional)</span>
                  <textarea
                    className="textarea"
                    rows={2}
                    value={q.quote ?? ''}
                    onChange={(e) => onChange({ quote: e.target.value })}
                    placeholder='e.g. Customer: "Is this available? How much does it cost?"'
                  />
                </label>
                <div className="field span-2">
                  <span>Image (optional)</span>
                  <ImageUploader value={q.imageId} onChange={(imageId) => onChange({ imageId })} />
                </div>
              </>
            ) : (
              <div className="span-2">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setShowMore(true)}
                >
                  + Add example quote or image
                </button>
              </div>
            )}
          </div>

          {issue && <p className="error-text">{issue}</p>}

          <div className="question-editor-foot">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onToggle}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OptionsEditor({
  options,
  onChange,
}: {
  options: string[];
  onChange: (o: string[]) => void;
}) {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusIndex, setFocusIndex] = useState<number>();

  // Focus the option that was just added or reached with the keyboard.
  useEffect(() => {
    if (focusIndex === undefined) return;
    inputs.current[focusIndex]?.focus();
    setFocusIndex(undefined);
  }, [focusIndex, options.length]);

  const normalized = options.map((o) => o.trim().toLowerCase());

  function insertAfter(i: number, values: string[]) {
    const next = [...options];
    next.splice(i + 1, 0, ...values);
    onChange(next);
    setFocusIndex(i + values.length);
  }

  function remove(i: number) {
    onChange(options.filter((_, j) => j !== i));
  }

  return (
    <div className="options-editor">
      {options.map((opt, i) => {
        const duplicate = opt.trim() !== '' && normalized.indexOf(normalized[i]!) !== i;
        return (
          <div key={i} className="option-row">
            <span className="option-bullet" aria-hidden>
              {i + 1}
            </span>
            <input
              ref={(el) => {
                inputs.current[i] = el;
              }}
              className="input"
              value={opt}
              aria-label={`Option ${i + 1}`}
              aria-invalid={duplicate ? true : undefined}
              title={duplicate ? 'This option is listed twice' : undefined}
              onChange={(e) => onChange(options.map((o, j) => (j === i ? e.target.value : o)))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  insertAfter(i, ['']);
                } else if (e.key === 'Backspace' && opt === '' && options.length > 1) {
                  e.preventDefault();
                  remove(i);
                  setFocusIndex(Math.max(0, i - 1));
                }
              }}
              onPaste={(e) => {
                // Pasting a list (one option per line) creates one option per line.
                const lines = e.clipboardData
                  .getData('text')
                  .split(/\r?\n/)
                  .map((l) => l.trim())
                  .filter(Boolean);
                if (lines.length < 2) return;
                e.preventDefault();
                const next = [...options];
                next.splice(i, 1, ...(opt.trim() ? [opt] : []), ...lines);
                onChange(next);
              }}
            />
            <button
              type="button"
              className="icon-btn"
              title="Move up"
              aria-label="Move option up"
              disabled={i === 0}
              onClick={() => onChange(moveItem(options, i, i - 1))}
            >
              ↑
            </button>
            <button
              type="button"
              className="icon-btn danger"
              title="Remove option"
              aria-label="Remove option"
              onClick={() => remove(i)}
            >
              ✕
            </button>
          </div>
        );
      })}
      <div className="row tight wrap">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => insertAfter(options.length - 1, [''])}
        >
          + Add option
        </button>
        {!normalized.includes('other') && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onChange([...options, 'Other'])}
          >
            + Add “Other”
          </button>
        )}
        {!normalized.some((o) => o.startsWith('not sure')) && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onChange([...options, 'Not sure yet'])}
          >
            + Add “Not sure yet”
          </button>
        )}
      </div>
      <p className="hint">
        Press Enter for a new option, or paste a list with one option per line.
      </p>
    </div>
  );
}
